import { baseApi } from '@/Redux/baseApi';

/**
 * `/guest-settings/branches/:branchId` — the three namespaces a venue points at
 * its QR guests, cached.
 *
 * Injected from beside the screen that owns them rather than added to
 * `Redux/Slices/operationsApi.ts`, the same seam `Pages/Operations/waiterStationApi.ts`
 * and `Pages/Spaces/kidsStationApi.ts` use: one `baseApi`, one tag namespace,
 * endpoints declared where they are read.
 *
 * The writes are mutations here rather than bare `api.patch` calls in the panel.
 * `baseApi`'s "deliberately NOT a write path" is about durable *order* writes,
 * which must survive a closed tab and therefore go through `@/lib/offline`; a
 * settings PATCH is a manager sitting at a desk, and what it needs instead is
 * for the read that every other screen shares to stop being stale the moment it
 * lands, which a mutation's tag invalidation does and a loose axios call does
 * not.
 *
 * ## What the read cannot tell you
 *
 * The GET answers the **effective** settings: the tenant's defaults with this
 * branch's overrides already folded over them, never the raw JSON. So nothing
 * in this module — and nothing on the screen — can say which value came from
 * which level. That is why the clear action is always offered rather than shown
 * only for a branch that has overrides: we do not know, and guessing would put
 * a button on screen that lies about whether it will do anything.
 */

/**
 * §15.3 — every one is phrased `show…`, so `false` is always the private answer
 * and a switch this dashboard fails to render can never widen what a venue
 * exposes. Mirrors `shared/domain/transparency.ts` on the API.
 */
export interface TransparencySettings {
  showEta: boolean;
  showItemStatus: boolean;
  showStationName: boolean;
  showDelayUpdates: boolean;
  showQueuePosition: boolean;
}

/** §14 — the PIN gate on joining a session that is already open. */
export interface GuestSessionSettings {
  requirePin: boolean;
  pinLength: number;
  maxPinAttempts: number;
  pinLockoutMinutes: number;
}

/** Whether a guest's own order waits for a waiter before the kitchen hears it. */
export interface GuestOrderingSettings {
  requireWaiterConfirmation: boolean;
  confirmationSlaSeconds: number;
}

export interface GuestSettings {
  transparency: TransparencySettings;
  session: GuestSessionSettings;
  ordering: GuestOrderingSettings;
}

/**
 * A PATCH body. Every level optional, because the endpoint leaves out what it is
 * not sent — see {@link changedSettings} for why that matters here.
 */
export interface GuestSettingsPatch {
  transparency?: Partial<TransparencySettings>;
  session?: Partial<GuestSessionSettings>;
  ordering?: Partial<GuestOrderingSettings>;
}

export interface Bound {
  min: number;
  max: number;
}

/**
 * The server's own `@Min`/`@Max` on `UpdateGuestSettingsDto`, restated so the
 * form can refuse a value before it is sent and say what the limit is.
 *
 * A second copy of a rule is a copy that can drift, and this one is worth the
 * risk: the API does not publish its bounds anywhere a browser can read, and
 * the alternative is a field that accepts `900` for a PIN length and answers
 * with a validation toast — which teaches the manager nothing about what number
 * would have worked. `pinLength` in particular is *clamped* rather than
 * rejected on the resolve path, so a bad value would come back silently
 * different from what was typed.
 */
export const GUEST_SETTING_BOUNDS = {
  pinLength: { min: 4, max: 8 },
  maxPinAttempts: { min: 1, max: 20 },
  pinLockoutMinutes: { min: 1, max: 240 },
  confirmationSlaSeconds: { min: 60, max: 3600 },
} as const satisfies Record<string, Bound>;

function changedKeys<T extends object>(baseline: T, draft: T): Partial<T> | undefined {
  const changed: Partial<T> = {};
  let any = false;
  for (const key of Object.keys(draft) as (keyof T)[]) {
    if (Object.is(baseline[key], draft[key])) continue;
    changed[key] = draft[key];
    any = true;
  }
  return any ? changed : undefined;
}

/**
 * The keys this edit actually moved, and nothing else.
 *
 * The failure being prevented is a whole-object write. `Branch.settings` is one
 * shared JSON column and the API merges what it is sent key by key, so a PATCH
 * carrying all twelve switches would stamp a manager's opened screen over
 * whatever a colleague changed in another namespace while it sat there — and
 * would do it silently, because every value it sends is a value that was true
 * when the screen loaded. Sending only what moved makes two people editing two
 * different namespaces a non-event.
 *
 * A namespace with nothing changed is omitted entirely rather than sent empty:
 * `{}` is a write of no keys, but it is still a write, and the audit log records
 * it as one.
 */
export function changedSettings(baseline: GuestSettings, draft: GuestSettings): GuestSettingsPatch {
  const patch: GuestSettingsPatch = {};
  const transparency = changedKeys(baseline.transparency, draft.transparency);
  if (transparency) patch.transparency = transparency;
  const session = changedKeys(baseline.session, draft.session);
  if (session) patch.session = session;
  const ordering = changedKeys(baseline.ordering, draft.ordering);
  if (ordering) patch.ordering = ordering;
  return patch;
}

export function hasChanges(patch: GuestSettingsPatch): boolean {
  return Object.keys(patch).length > 0;
}

export const guestSettingsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET` — `settings.view`, and branch-scoped on top of it. */
    guestSettings: build.query<GuestSettings, string>({
      query: (branchId) => ({ url: `/guest-settings/branches/${branchId}` }),
      providesTags: (_result, _error, branchId) => [{ type: 'GuestSettings' as const, id: branchId }],
    }),

    /** `PATCH` — `settings.update`. Body from {@link changedSettings}, never a full blob. */
    updateGuestSettings: build.mutation<
      GuestSettings,
      { branchId: string; patch: GuestSettingsPatch }
    >({
      query: ({ branchId, patch }) => ({
        url: `/guest-settings/branches/${branchId}`,
        method: 'patch',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { branchId }) => [
        { type: 'GuestSettings' as const, id: branchId },
      ],
    }),

    /**
     * `DELETE` — drops the branch's overrides so it *inherits*. Deliberately not
     * expressible as a PATCH of the current defaults: that would write an
     * opinion at branch level, and the branch would then sit out every later
     * change the venue makes. Two different outcomes, one of which looks
     * identical on screen the day it is done.
     */
    clearGuestSettings: build.mutation<GuestSettings, string>({
      query: (branchId) => ({ url: `/guest-settings/branches/${branchId}`, method: 'delete' }),
      invalidatesTags: (_result, _error, branchId) => [
        { type: 'GuestSettings' as const, id: branchId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGuestSettingsQuery,
  useUpdateGuestSettingsMutation,
  useClearGuestSettingsMutation,
} = guestSettingsApi;
